import {
  ContainerElement,
  ElementType,
  FieldElement,
  FieldType,
  IFormDefinition,
  IFormOptions,
  IFormSchema,
  InsertMode,
  Layout,
  LayoutBreakpoints,
  LayoutElement,
  LayoutSize,
  getFieldDef,
  getSizeByBreakpoint,
  isBooleanFieldElement,
  isColumnElement,
  isFieldElement,
  isImageElement,
  isLayoutTableFieldElement,
  isOptionsFieldElement,
  isSectionElement,
  isTexElement,
  normalizePath,
} from "@entaconsulting/form";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { isEmpty, isNumber, isObject, set, toPath } from "lodash";
import {
  fetchDeveloperForm,
  saveFormDefinition,
} from "store/form/formDataSlice";
import { FormDefinition } from "../../formsCRUD/store/formDefinitionsSlice";
import generateFieldDefinition from "../components/editorUtils/generateFieldDefinition";
import generateRenamedLayoutObject from "../components/editorUtils/generateRenamedLayoutObject";
import generateRenamedSchemaObject from "../components/editorUtils/generateRenamedSchemaObject";
import generateSchemaObject from "../components/jsonSchema/generateSchemaObject";
import resolveLayoutElement from "../components/jsonSchema/resolveJsonElement";
import {
  BOOLEAN_FIELD_VARIANT,
  BOOLEAN_FIELD_VARIANT_DEFAULT,
  OPTIONS_FIELD_VARIANT,
  OPTIONS_FIELD_VARIANT_DEFAULT,
} from "../constants";
import { ColumnEdited } from "../types";
import calculateNewElementSize from "../utils/calculateNewElementSize";
import { generateLayoutElement } from "../utils/generateLayoutElement";
import generateNewFieldName from "../utils/generateNewFieldName";
import generatePlaceForNewElement from "../utils/generatePlaceForNewElements";
import getInsertByMode from "../utils/getInsertByMode";
import getLayoutElementAtPath from "../utils/getLayoutElementAtPath";
import getLayoutElements from "../utils/getLayoutElements";
import getMostGenericBreakpoint from "../utils/getMostGenericBreakpoint";
import getRowPositions from "../utils/getRowPositions";
import resizeRowElements from "../utils/resizeRowElements";
import searchAndDeleteFieldDefinitions from "../utils/searchAndDeleteFieldDefinitions";

type EditorState = {
  formBeingEdited?: FormDefinition;
  formDefinitionPreview: IFormDefinition;
  schemaJsonEdited: string;
  layoutJsonEdited: string;
  optionsJsonEdited: string;
  schemaObject: unknown;
  fieldNameSuggestionsObject?: string[];
  isInvalidJsonSchema: boolean;
  isInvalidJsonLayout: boolean;
  isInvalidJsonOptions: boolean;
  isDirty: boolean;
  selectedRoute: string;
  initialSelectedRoute: string;
  confirmDelete: boolean;
  confirmSaveWithErrors: boolean;
  newestFieldName: string;
  columnEdited: ColumnEdited | null;
  isAsyncRenderForm: boolean;
};

const initialState: EditorState = {
  formDefinitionPreview: {
    schema: {},
    layout: { elements: [] },
    options: {},
  },
  schemaJsonEdited: "",
  layoutJsonEdited: "",
  optionsJsonEdited: "",
  schemaObject: null,
  isInvalidJsonLayout: false,
  isInvalidJsonOptions: false,
  isInvalidJsonSchema: false,
  isDirty: false,
  selectedRoute: "",
  initialSelectedRoute: "",
  confirmDelete: false,
  confirmSaveWithErrors: false,
  newestFieldName: "",
  columnEdited: null,
  isAsyncRenderForm: false,
};

const processSchemaChanges = (jsonSchema: string, state: EditorState) => {
  let editedSchema: IFormSchema;

  try {
    editedSchema = JSON.parse(jsonSchema);
  } catch (_) {
    state.isInvalidJsonSchema = true;
    return state;
  }
  const { schemaObject, layoutSuggestionsObject } = generateSchemaObject(
    "",
    editedSchema
  );
  state.schemaObject = schemaObject;
  state.fieldNameSuggestionsObject = layoutSuggestionsObject;

  if (!state.isDirty) state.isDirty = true;

  state.isInvalidJsonSchema = false;
  state.formDefinitionPreview.schema = editedSchema;
  state.schemaJsonEdited = jsonSchema;
  return state;
};

const processLayoutChanges = (layoutSchema: string, state: EditorState) => {
  let editedLayout: Layout;
  try {
    editedLayout = JSON.parse(layoutSchema);
  } catch (_) {
    state.isInvalidJsonLayout = true;
    return;
  }
  if (!state.isDirty) state.isDirty = true;

  state.isInvalidJsonLayout = false;
  state.formDefinitionPreview.layout = editedLayout;
  state.layoutJsonEdited = layoutSchema;
};

export const editorSlice = createSlice({
  name: "developer/edit/editor",
  initialState,
  reducers: {
    changeLayoutElementProp(
      state,
      action: PayloadAction<{
        targetProp: string;
        value: any;
      }>
    ) {
      const { targetProp, value } = action.payload;
      const elementPath = state.selectedRoute;

      let layoutObject: Layout = JSON.parse(state.layoutJsonEdited);

      let element = getLayoutElementAtPath(elementPath, layoutObject);

      set(element, targetProp, value);
      processLayoutChanges(JSON.stringify(layoutObject, null, 2), state);
      state.isAsyncRenderForm = true;
    },
    changeFieldDefinitionProp(
      state,
      action: PayloadAction<{
        targetProp: string;
        value: any;
      }>
    ) {
      const { targetProp, value } = action.payload;
      const elementPath = state.selectedRoute;
      const columnEdited = state.columnEdited;

      let layoutObject: Layout = JSON.parse(state.layoutJsonEdited);
      let schemaObject: IFormSchema = JSON.parse(state.schemaJsonEdited);

      let element = getLayoutElementAtPath(elementPath, layoutObject);

      if (!isFieldElement(element)) return;

      const targetElement =
        isLayoutTableFieldElement(element) && columnEdited
          ? element.listFields.fields[columnEdited.index]
          : element;

      const targetName =
        isLayoutTableFieldElement(element) && columnEdited
          ? `${element.name}.${columnEdited.name}`
          : element.name;

      const _targetProp =
        isLayoutTableFieldElement(element) && columnEdited
          ? toPath(targetProp).at(-1)!
          : targetProp;

      const fieldDef = getFieldDef(schemaObject, targetName);

      if (!fieldDef) return;

      const newFieldDefinition = generateFieldDefinition({
        ...fieldDef,
        type: _targetProp === "type" ? value : fieldDef.type,
      });

      if (isOptionsFieldElement(targetElement, newFieldDefinition)) {
        const hasVariant = OPTIONS_FIELD_VARIANT.includes(
          targetElement.variant!
        );
        const variant = hasVariant
          ? targetElement.variant
          : OPTIONS_FIELD_VARIANT_DEFAULT;

        set(targetElement, "variant", variant);
        processLayoutChanges(JSON.stringify(layoutObject, null, 2), state);
      }

      if (isBooleanFieldElement(targetElement, newFieldDefinition)) {
        const hasVariant = BOOLEAN_FIELD_VARIANT.includes(
          targetElement.variant!
        );
        const variant = hasVariant
          ? targetElement.variant
          : OPTIONS_FIELD_VARIANT_DEFAULT;
        set(targetElement, "variant", variant);
        processLayoutChanges(JSON.stringify(layoutObject, null, 2), state);
      }

      set(newFieldDefinition, _targetProp, value);

      const insertionPath =
        toPath(targetName).length > 1
          ? toPath(targetName).join(".schema.")
          : targetName;

      set(schemaObject, insertionPath, newFieldDefinition);

      processSchemaChanges(JSON.stringify(schemaObject, null, 2), state);
      state.isAsyncRenderForm = true;
    },
    renameFieldElement(
      state,
      action: PayloadAction<{
        targetProp: string;
        value: any;
      }>
    ) {
      const { targetProp, value } = action.payload;
      const elementPath = state.selectedRoute;

      // This function creates a closure and we need the
      // latest value so we use a ref
      let layoutObject: Layout = JSON.parse(state.layoutJsonEdited);
      let schemaObject: IFormSchema = JSON.parse(state.schemaJsonEdited);

      let element = getLayoutElementAtPath(elementPath, layoutObject);

      if (isFieldElement(element)) {
        const oldNameNormalized = normalizePath(element.name);
        const newNameNormalized = normalizePath(value);

        let newSchemaObject = generateRenamedSchemaObject(
          schemaObject,
          toPath(oldNameNormalized),
          toPath(newNameNormalized)
        );

        let newLayoutObject = generateRenamedLayoutObject(
          layoutObject,
          oldNameNormalized,
          newNameNormalized
        );

        layoutObject = newLayoutObject;
        schemaObject = newSchemaObject;

        const fieldDef = getFieldDef(schemaObject, value)!;

        const newFieldDefinition = generateFieldDefinition(fieldDef);

        const newNameNormalizedPath = toPath(value);

        const insertionPath =
          newNameNormalizedPath.length > 1
            ? newNameNormalizedPath.join(".schema.")
            : newNameNormalized;

        set(schemaObject, insertionPath, newFieldDefinition);
      }

      set(element, targetProp, value);
      processSchemaChanges(JSON.stringify(schemaObject, null, 2), state);
      processLayoutChanges(JSON.stringify(layoutObject, null, 2), state);
    },
    renameColumnElement(
      state,
      action: PayloadAction<{
        targetProp: string;
        value: any;
      }>
    ) {
      const { targetProp, value } = action.payload;
      const elementPath = state.selectedRoute;
      const columnEdited = state.columnEdited;

      // This function creates a closure and we need the
      // latest value so we use a ref
      let layoutObject: Layout = JSON.parse(state.layoutJsonEdited);
      let schemaObject: IFormSchema = JSON.parse(state.schemaJsonEdited);

      let element = getLayoutElementAtPath(elementPath, layoutObject);

      if (isLayoutTableFieldElement(element) && columnEdited) {
        const newName = value;
        const index = columnEdited.index;

        if (isNumber(index)) {
          const oldName = element.listFields.fields[index].name;
          const oldNamePath = toPath(`${element.name}.${oldName}`);
          const newNamePath = toPath(`${element.name}.${newName}`);

          const newSchemaObject = generateRenamedSchemaObject(
            schemaObject,
            oldNamePath,
            newNamePath
          );

          schemaObject = newSchemaObject;
          columnEdited.name = newName;
        }
      }

      set(element, targetProp, value);
      processSchemaChanges(JSON.stringify(schemaObject, null, 2), state);
      processLayoutChanges(JSON.stringify(layoutObject, null, 2), state);
    },
    formDefinitionSchemaChanged: (state, action: PayloadAction<string>) => {
      processSchemaChanges(action.payload, state);
    },
    formDefinitionLayoutChanged: (state, action: PayloadAction<string>) => {
      processLayoutChanges(action.payload, state);
    },
    formDefinitionOptionsChanged: (state, action: PayloadAction<string>) => {
      const jsonOptions = action.payload;
      let editedOptions: IFormOptions;
      try {
        editedOptions = JSON.parse(jsonOptions);
      } catch (_) {
        state.isInvalidJsonOptions = true;
        return;
      }

      if (!state.isDirty) state.isDirty = true;

      state.isInvalidJsonOptions = false;
      state.formDefinitionPreview.options = editedOptions;
      state.optionsJsonEdited = jsonOptions;
    },
    setFormDefinitionPreviewDirty: (state, action: PayloadAction<boolean>) => {
      state.isDirty = action.payload;
    },
    setSchemaObject: (state, action: PayloadAction<unknown>): void => {
      state.schemaObject = action.payload;
    },
    setIsAsyncRenderForm: (state, action: PayloadAction<boolean>): void => {
      state.isAsyncRenderForm = action.payload;
    },
    insertElement: (
      state,
      action: PayloadAction<{
        routeToElement: string;
        insertAt: number;
        mode: InsertMode;
        elementType: ElementType | "table";
        fieldType?: FieldType;
        activeBreakpoint: NonNullable<LayoutBreakpoints>;
      }>
    ) => {
      const {
        routeToElement,
        insertAt,
        mode,
        elementType,
        fieldType,
        activeBreakpoint,
      } = action.payload;

      const formDefinitionPreview = state.formDefinitionPreview;

      let elements = getLayoutElements(routeToElement, formDefinitionPreview)!;

      const newElement = generateLayoutElement(elementType);

      const isFieldEl = ["field", "table"].includes(elementType);

      if (isFieldEl && fieldType) {
        const isTableFieldElement = isLayoutTableFieldElement(newElement);
        const newFieldDefinition = generateFieldDefinition({
          type: fieldType,
        });
        const newName = generateNewFieldName(formDefinitionPreview, {
          isTableElement: isTableFieldElement,
        });
        const element = newElement as FieldElement;
        element.name = newName;
        formDefinitionPreview.schema[newName] = newFieldDefinition;

        if (isOptionsFieldElement(element, newFieldDefinition)) {
          element.variant = OPTIONS_FIELD_VARIANT_DEFAULT;
        } else if (isBooleanFieldElement(element, newFieldDefinition)) {
          element.variant = BOOLEAN_FIELD_VARIANT_DEFAULT;
        }

        state.newestFieldName = newName;
      } else {
        state.newestFieldName = "";
      }

      const isRequiredSpace =
        isFieldElement(newElement) ||
        isImageElement(newElement) ||
        isTexElement(newElement) ||
        isColumnElement(newElement);

      var modifiedFields: Record<string, any> = {};

      if (isRequiredSpace) {
        if (mode === "row") {
          // FullRow
          newElement.size = 12;
        } else {
          // SameRow
          const { rowStart, rowEnd } = getRowPositions(
            elements,
            insertAt,
            activeBreakpoint
          );

          const size = calculateNewElementSize(rowStart, rowEnd, true);

          newElement.size = size as LayoutSize;

          modifiedFields = generatePlaceForNewElement(
            size,
            elements,
            insertAt,
            rowStart,
            rowEnd,
            activeBreakpoint
          );
        }
      }

      Object.entries(modifiedFields).forEach(([key, value]) => {
        elements[+key] = value;
      });

      const _insertAt =
        isRequiredSpace && mode === "colAfter" ? insertAt + 1 : insertAt;

      elements.splice(_insertAt, 0, newElement);

      state.isDirty = true;
      state.selectedRoute = `${routeToElement}/${_insertAt}`;
      state.initialSelectedRoute = state.selectedRoute;
      state.columnEdited = null;
      state.isAsyncRenderForm = false;

      processLayoutChanges(
        JSON.stringify(state.formDefinitionPreview.layout, null, 2),
        state
      );
      processSchemaChanges(
        JSON.stringify(state.formDefinitionPreview.schema, null, 2),
        state
      );
    },
    onMoveElement: (
      state,
      action: PayloadAction<{
        srcPath: string;
        destPath: string;
        insertAt: number;
        mode: InsertMode;
        activeBreakpoint: NonNullable<LayoutBreakpoints>;
      }>
    ): void => {
      const { destPath, srcPath, insertAt, mode, activeBreakpoint } =
        action.payload;

      // Get source and destination elements
      const srcContainerElementPath = srcPath.split("/").slice(0, -1).join("/");

      const sourceElements = getLayoutElements(
        srcContainerElementPath,
        state.formDefinitionPreview
      );
      const destinationElements = getLayoutElements(
        destPath,
        state.formDefinitionPreview
      );

      if (!sourceElements || !destinationElements) return;

      // Get dragged element
      const [srcElementIndex] = srcPath.split("/").slice(-1);
      const draggedElement = sourceElements[+srcElementIndex] as FieldElement;
      const newElement = { ...draggedElement };

      // Get row positions
      const { rowStart, rowEnd } = getRowPositions(
        destinationElements,
        insertAt,
        activeBreakpoint
      );

      const isFullRowModeInsertion = mode === "row";

      // Adjust offset depending of insertion place and insertion mode
      const isSameContainerElement = sourceElements === destinationElements;

      const isSameRow =
        isSameContainerElement &&
        +srcElementIndex >= rowStart &&
        +srcElementIndex <= rowEnd;

      let adjustedRowStart = rowStart;
      let adjustedRowEnd = rowEnd;
      let adjustedInsertAt = getInsertByMode(mode, insertAt);

      if (isSameContainerElement) {
        if (isSameRow) {
          if (
            +srcElementIndex === adjustedInsertAt ||
            +srcElementIndex === adjustedInsertAt - 1
          ) {
            return;
          }
          if (+srcElementIndex < adjustedInsertAt) {
            adjustedInsertAt -= 1;
          }
        } else {
          if (+srcElementIndex <= rowStart) {
            adjustedRowStart -= 1;
            adjustedInsertAt -= 1;
          }
          if (+srcElementIndex >= rowEnd) {
            adjustedRowEnd += 1;
          }
        }
      } else {
        adjustedRowEnd += 1;
      }

      // Assign new size to new element
      const newSize = isFullRowModeInsertion
        ? 12
        : calculateNewElementSize(adjustedRowStart, adjustedRowEnd);

      if (isNumber(newElement.size) || isEmpty(newElement.size)) {
        newElement.size = newSize as LayoutSize;
      }

      // Element with breakpoints
      if (isObject(newElement.size)) {
        const mostGenericBreakpoint = getMostGenericBreakpoint(newElement.size);
        set(newElement.size, mostGenericBreakpoint, newSize);
      }

      // Remove and insert new element
      sourceElements.splice(+srcElementIndex, 1);
      destinationElements.splice(adjustedInsertAt, 0, newElement);

      // Resize row elements if needed
      if (!isFullRowModeInsertion && !isSameRow) {
        const size = getSizeByBreakpoint(newElement.size, activeBreakpoint);
        resizeRowElements(
          size,
          destinationElements,
          adjustedInsertAt,
          adjustedRowStart,
          adjustedRowEnd,
          activeBreakpoint
        );
      }

      processLayoutChanges(
        JSON.stringify(state.formDefinitionPreview.layout, null, 2),
        state
      );

      state.isDirty = true;
      state.selectedRoute = "";
      state.isAsyncRenderForm = false;
    },
    deleteElement: (state): void => {
      const formDefinitionPreview = state.formDefinitionPreview;
      const selectedRoute = state.selectedRoute;

      const routeToElement = selectedRoute.split("/").slice(1);

      const deleteAt = routeToElement.pop() as string;

      let elements: LayoutElement[];

      if (selectedRoute.startsWith("body")) {
        const container = resolveLayoutElement(
          formDefinitionPreview.layout.elements,
          routeToElement
        ) as ContainerElement;
        elements = container?.elements ?? formDefinitionPreview.layout.elements;
      } else {
        const container = resolveLayoutElement(
          formDefinitionPreview.layout.header?.elements!,
          routeToElement
        ) as ContainerElement;
        elements =
          container?.elements ?? formDefinitionPreview.layout.header?.elements;
      }

      const elementToDelete = elements[+deleteAt];

      if (
        (isColumnElement(elementToDelete) ||
          isSectionElement(elementToDelete)) &&
        !state.confirmDelete &&
        elementToDelete.elements.length > 0
      ) {
        state.confirmDelete = true;
        return;
      }

      const [elementDeleted] = elements.splice(+deleteAt, 1);

      searchAndDeleteFieldDefinitions(
        formDefinitionPreview.schema,
        elementDeleted
      );

      state.isDirty = true;
      state.selectedRoute = "";
      state.initialSelectedRoute = state.selectedRoute;
      state.confirmDelete = false;
      state.isAsyncRenderForm = false;

      processLayoutChanges(
        JSON.stringify(state.formDefinitionPreview.layout, null, 2),
        state
      );
      processSchemaChanges(
        JSON.stringify(state.formDefinitionPreview.schema, null, 2),
        state
      );
    },
    setConfirmDelete: (state, action: PayloadAction<boolean>): void => {
      state.confirmDelete = action.payload;
    },
    setSelectedRoute: (state, action: PayloadAction<string>): void => {
      state.selectedRoute = action.payload;
      state.newestFieldName = "";
      state.columnEdited = null;
    },
    requireSaveWithErrorsConfirmation: (state): void => {
      state.confirmSaveWithErrors = true;
    },
    cancelSaveWithErrorsConfirmation: (state): void => {
      state.confirmSaveWithErrors = false;
    },
    setColumnEdited: (
      state,
      action: PayloadAction<ColumnEdited | null>
    ): void => {
      state.columnEdited = action.payload;
    },
    clearFormEdited: () => initialState,
    clearVisualEditorSelectionState: (state) => {
      state.selectedRoute = "";
      state.initialSelectedRoute = "";
    },
    changeLayoutExpressionAtLine: (
      state,
      action: PayloadAction<{
        targetExpressionLine: number;
        value: string;
      }>
    ): void => {
      const { value, targetExpressionLine } = action.payload;

      const jsonLines = state.layoutJsonEdited.split(/\r\n|\r|\n/);
      const targetLine = jsonLines[targetExpressionLine];
      let newLine = targetLine.replace(
        /^(\s*)"(.*)"\s*:\s*(?:(".*")|true|false)*/,
        `$1"$2": ${JSON.stringify(value)}`
      );
      jsonLines[targetExpressionLine] = newLine;
      processLayoutChanges(jsonLines.join("\n"), state);
    },
    changeSchemaExpressionAtLine: (
      state,
      action: PayloadAction<{
        targetExpressionLine: number;
        value: string;
      }>
    ): void => {
      const { value, targetExpressionLine } = action.payload;

      const jsonLines = state.schemaJsonEdited.split(/\r\n|\r|\n/);
      const targetLine = jsonLines[targetExpressionLine];
      let newLine = targetLine.replace(
        /^(\s*)"(.*)"\s*:\s*(?:(".*")|true|false)*/,
        `$1"$2": ${JSON.stringify(value)}`
      );
      jsonLines[targetExpressionLine] = newLine;
      processSchemaChanges(jsonLines.join("\n"), state);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveFormDefinition.fulfilled, (state) => {
        state.isDirty = false;
      })
      .addCase(fetchDeveloperForm.fulfilled, (state, action) => {
        const formDefinitionMeta = action.payload.dynamicFormDefinition;
        const formDefinition: IFormDefinition =
          formDefinitionMeta.formDefinition;

        const formBeingEdited: FormDefinition = {
          id: formDefinitionMeta.id,
          name: formDefinitionMeta.name,
          description: formDefinitionMeta.description,
          disabled: formDefinitionMeta.disabled,
          workspaceId: formDefinitionMeta.workspaceId,
        };

        state.formBeingEdited = formBeingEdited;

        if (state.formDefinitionPreview.schema !== formDefinition.schema) {
          const { schemaObject, layoutSuggestionsObject } =
            generateSchemaObject("", formDefinition.schema);
          state.schemaObject = schemaObject;
          state.fieldNameSuggestionsObject = layoutSuggestionsObject;
        }
        state.formDefinitionPreview = formDefinition;

        state.schemaJsonEdited = JSON.stringify(
          formDefinition.schema,
          null,
          "\t"
        );
        state.layoutJsonEdited = JSON.stringify(
          formDefinition.layout,
          null,
          "\t"
        );
        state.optionsJsonEdited = JSON.stringify(
          formDefinition.options,
          null,
          "\t"
        );
      });
  },
});

export const {
  clearFormEdited,
  formDefinitionLayoutChanged,
  formDefinitionOptionsChanged,
  formDefinitionSchemaChanged,
  setFormDefinitionPreviewDirty,
  insertElement,
  setColumnEdited,
  setSelectedRoute,
  deleteElement,
  setConfirmDelete,
  setSchemaObject,
  requireSaveWithErrorsConfirmation,
  cancelSaveWithErrorsConfirmation,
  onMoveElement,
  clearVisualEditorSelectionState,
  changeLayoutElementProp,
  changeFieldDefinitionProp,
  renameFieldElement,
  renameColumnElement,
  changeLayoutExpressionAtLine,
  changeSchemaExpressionAtLine,
  setIsAsyncRenderForm,
} = editorSlice.actions;
